/** Q1: asset counts (verification). Field names match FixedAssetCountDtos on the server. */

export type CountStatus = 'OPEN' | 'CLOSED' | 'CANCELLED';
export type CountResult = 'PENDING' | 'FOUND' | 'WRONG_LOCATION' | 'WRONG_CUSTODIAN' | 'UNEXPECTED' | 'NOT_FOUND';
export type AssetCondition = 'GOOD' | 'DAMAGED' | 'NOT_IN_USE';

export const COUNT_RESULTS: CountResult[] =
  ['PENDING', 'FOUND', 'WRONG_LOCATION', 'UNEXPECTED', 'NOT_FOUND']; // WRONG_CUSTODIAN stays a server value but is not offered

export const COUNT_RESULT_LABELS: Record<CountResult, string> = {
  PENDING: 'Pending',
  FOUND: 'Found',
  WRONG_LOCATION: 'Wrong location',
  WRONG_CUSTODIAN: 'Wrong custodian',
  UNEXPECTED: 'Unexpected',
  NOT_FOUND: 'Not found',
};

export const COUNT_RESULT_COLORS: Record<CountResult, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  PENDING: 'default',
  FOUND: 'success',
  WRONG_LOCATION: 'warning',
  WRONG_CUSTODIAN: 'warning',
  UNEXPECTED: 'info',
  NOT_FOUND: 'error',
};

export const CONDITION_LABELS: Record<AssetCondition, string> = {
  GOOD: 'Good',
  DAMAGED: 'Damaged',
  NOT_IN_USE: 'Not in use',
};

export interface AssetCount {
  id: string;
  institutionId: string;
  refNo: string;
  name: string;
  status: CountStatus;
  locationIds: string[];
  locationNames: string[];
  departmentIds: string[];
  departmentNames: string[];
  startedAt?: string | null;
  startedBy?: string | null;
  startedByName?: string | null;
  closedAt?: string | null;
  closedBy?: string | null;
  closedByName?: string | null;
  notes?: string | null;
  totalLines: number;
  resultCounts: Partial<Record<CountResult, number>>;
}

export interface AssetCountLine {
  id: string;
  countId: string;
  fixedAssetId: string;
  assetCode: string;
  assetName: string;
  expectedLocationId?: string | null;
  expectedLocationName?: string | null;
  expectedDepartmentId?: string | null;
  expectedDepartmentName?: string | null;
  expectedCustodianUserId?: string | null;
  expectedCustodianName?: string | null;
  result: CountResult;
  countedLocationId?: string | null;
  countedLocationName?: string | null;
  countedCustodianUserId?: string | null;
  countedCustodianName?: string | null;
  condition?: AssetCondition | null;
  note?: string | null;
  scannedAt?: string | null;
  scannedBy?: string | null;
}

export interface StartCountRequest {
  institutionId: string;
  name: string;
  locationIds?: string[];
  departmentIds?: string[];
  notes?: string;
}

/** Scan by {@code assetId} (from a QR) or by {@code assetCode} (typed or USB scanner). */
export interface ScanCountRequest {
  assetId?: string;
  assetCode?: string;
  countedLocationId?: string;
  condition?: AssetCondition;
  note?: string;
}

export interface LineUpdateRequest {
  condition?: AssetCondition | null;
  note?: string;
}

export type LabelLayout = 'A4_3x7' | 'A4_2x5' | 'SINGLE_50x25';

export const LABEL_LAYOUT_OPTIONS: { value: LabelLayout; label: string; perPage: number }[] = [
  { value: 'A4_3x7', label: 'A4 sheet, 3 x 7 (63.5 x 38.1 mm)', perPage: 21 },
  { value: 'A4_2x5', label: 'A4 sheet, 2 x 5 (99.1 x 57 mm)', perPage: 10 },
  { value: 'SINGLE_50x25', label: 'Single label printer, 50 x 25 mm', perPage: 1 },
];

export interface LabelRequest {
  institutionId: string;
  assetIds: string[];
  layout: LabelLayout;
  startPosition?: number;
  origin?: string;
}

export interface ScanResolution {
  type: string;
  id: string;
  institutionId: string;
  path: string;
}
